import { useEffect, useMemo, useState, type ChangeEvent } from "react"
import { Textarea } from "@/components/ui/textarea"
import { cn } from "@/lib/cn"

type ToneDepth = "concise" | "balanced" | "thorough"
type ModifierKey = "extra_empathy" | "proactive_next_step" | "plain_language" | "confident_when_clear"
type PresetKey =
  | "custom"
  | "everyday_casual"
  | "warm_neighbor"
  | "cheerful_helper"
  | "calm_guide"
  | "clear_support"
  | "patient_coach"
  | "polished_concierge"
  | "practical_specialist"
  | "professional_partner"
  | "executive_formal"

type TextBinding = {
  value: string
  onChange: (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void
  onBlur: () => void
}

type Props = {
  binding: TextBinding
  onPresetSave: (text: string) => void
}

const PRESETS: Array<{
  key: Exclude<PresetKey, "custom">
  number: number
  name: string
  instruction: string
}> = [
  {
    key: "everyday_casual",
    number: 1,
    name: "Everyday Casual",
    instruction: "Sound relaxed, friendly, and plainspoken. Use contractions naturally, keep the language human, and never become sloppy or overly familiar.",
  },
  {
    key: "warm_neighbor",
    number: 2,
    name: "Warm Neighbor",
    instruction: "Sound extra kind, reassuring, and gently conversational. Make the user feel heard while still moving the conversation forward.",
  },
  {
    key: "cheerful_helper",
    number: 3,
    name: "Cheerful Helper",
    instruction: "Sound upbeat and encouraging without hype, exclamation-heavy copy, or emojis. Keep the energy light, useful, and grounded.",
  },
  {
    key: "calm_guide",
    number: 4,
    name: "Calm Guide",
    instruction: "Sound steady, patient, and low-drama. Be especially clear with confused users and make each next step feel manageable.",
  },
  {
    key: "clear_support",
    number: 5,
    name: "Clear Support",
    instruction: "Sound direct, helpful, and user-first. Prioritize clarity, fast orientation, and answers that feel easy to act on.",
  },
  {
    key: "patient_coach",
    number: 6,
    name: "Patient Coach",
    instruction: "Sound like a patient coach. Explain steps clearly, teach just enough context to help the user succeed, and avoid talking down to them.",
  },
  {
    key: "polished_concierge",
    number: 7,
    name: "Polished Concierge",
    instruction: "Sound warm, attentive, and slightly elevated, like a polished concierge. Be gracious and precise without becoming stiff.",
  },
  {
    key: "practical_specialist",
    number: 8,
    name: "Practical Specialist",
    instruction: "Sound knowledgeable, efficient, and specific while staying warm. Lead with the useful answer, then add only the context that helps.",
  },
  {
    key: "professional_partner",
    number: 9,
    name: "Professional Partner",
    instruction: "Sound businesslike, concise, confident, and collaborative. Treat the user like a capable partner and keep the conversation outcome-focused.",
  },
  {
    key: "executive_formal",
    number: 10,
    name: "Executive Formal",
    instruction: "Sound crisp, restrained, and polished. Use minimal casual language, stay concise, and keep the tone professional without sounding cold.",
  },
]

const DEPTHS: Array<{ key: ToneDepth; label: string; instruction: string }> = [
  { key: "concise", label: "Concise", instruction: "Keep replies brief and focused; answer first and avoid extra explanation unless the user asks." },
  { key: "balanced", label: "Balanced", instruction: "Give enough context to be useful while keeping replies easy to scan." },
  { key: "thorough", label: "Thorough", instruction: "Be more complete when the issue is nuanced; include relevant details, caveats, and next steps without rambling." },
]

const MODIFIERS: Array<{ key: ModifierKey; label: string; instruction: string }> = [
  { key: "extra_empathy", label: "Extra empathy", instruction: "Acknowledge frustration or uncertainty briefly before solving." },
  { key: "proactive_next_step", label: "Proactive next step", instruction: "End with the most useful next step when one is clear." },
  { key: "plain_language", label: "Plain language", instruction: "Use simple everyday wording and avoid jargon where possible." },
  { key: "confident_when_clear", label: "Confident when clear", instruction: "When the answer is known, state it confidently instead of hedging." },
]

const DEFAULT_DEPTH: ToneDepth = "balanced"
const DEFAULT_MODIFIERS: ModifierKey[] = ["plain_language"]

export function VoiceToneBuilder({ binding, onPresetSave }: Props) {
  const parsed = useMemo(() => parseGeneratedValue(binding.value), [binding.value])
  const [presetKey, setPresetKey] = useState<PresetKey>(parsed.presetKey)
  const [depth, setDepth] = useState<ToneDepth>(parsed.depth)
  const [modifiers, setModifiers] = useState<ModifierKey[]>(parsed.modifiers)

  useEffect(() => {
    setPresetKey(parsed.presetKey)
    setDepth(parsed.depth)
    setModifiers(parsed.modifiers)
  }, [parsed.presetKey, parsed.depth, parsed.modifiers])

  function saveGenerated(nextPreset: Exclude<PresetKey, "custom">, nextDepth: ToneDepth, nextModifiers: ModifierKey[]) {
    setPresetKey(nextPreset)
    setDepth(nextDepth)
    setModifiers(nextModifiers)
    onPresetSave(composeVoiceTone(nextPreset, nextDepth, nextModifiers))
  }

  function selectPreset(next: PresetKey) {
    setPresetKey(next)
    if (next === "custom") return
    saveGenerated(next, depth, modifiers)
  }

  function selectDepth(next: ToneDepth) {
    setDepth(next)
    if (presetKey === "custom") return
    saveGenerated(presetKey, next, modifiers)
  }

  function toggleModifier(key: ModifierKey) {
    const next = modifiers.includes(key) ? modifiers.filter((m) => m !== key) : [...modifiers, key]
    setModifiers(next)
    if (presetKey === "custom") return
    saveGenerated(presetKey, depth, next)
  }

  function onManualChange(e: ChangeEvent<HTMLTextAreaElement>) {
    setPresetKey("custom")
    binding.onChange(e)
  }

  return (
    <div className="space-y-3">
      <div className="grid gap-3 lg:grid-cols-[minmax(220px,0.9fr)_minmax(150px,0.45fr)_minmax(0,1.65fr)]">
        <label className="flex min-w-0 flex-col gap-1.5">
          <span className="text-xs font-medium text-app-muted">Preset</span>
          <select
            value={presetKey}
            onChange={(e) => selectPreset(e.target.value as PresetKey)}
            className="min-h-10 rounded-control border border-border bg-white/5 px-3 text-sm text-app-text outline-none transition focus:border-brand-red/50"
          >
            <option value="custom">Custom</option>
            {PRESETS.map((preset) => (
              <option key={preset.key} value={preset.key}>
                {preset.number}. {preset.name}
              </option>
            ))}
          </select>
        </label>

        <label className="flex min-w-0 flex-col gap-1.5">
          <span className="text-xs font-medium text-app-muted">Depth</span>
          <select
            value={depth}
            onChange={(e) => selectDepth(e.target.value as ToneDepth)}
            className="min-h-10 rounded-control border border-border bg-white/5 px-3 text-sm text-app-text outline-none transition focus:border-brand-red/50"
          >
            {DEPTHS.map((option) => (
              <option key={option.key} value={option.key}>
                {option.label}
              </option>
            ))}
          </select>
        </label>

        <div className="min-w-0 space-y-1.5">
          <div className="text-xs font-medium text-app-muted">Modifiers</div>
          <div className="flex flex-wrap gap-2">
            {MODIFIERS.map((modifier) => (
              <label
                key={modifier.key}
                className={cn(
                  "inline-flex min-h-10 cursor-pointer items-center gap-2 rounded-control border px-3 text-sm transition",
                  modifiers.includes(modifier.key)
                    ? "border-brand-red/50 bg-brand-red/10 text-app-text"
                    : "border-border bg-white/5 text-app-muted hover:bg-white/10 hover:text-app-text",
                )}
              >
                <input
                  type="checkbox"
                  checked={modifiers.includes(modifier.key)}
                  onChange={() => toggleModifier(modifier.key)}
                  className="h-4 w-4 rounded border-border bg-app-bg text-brand-red focus:ring-brand-red/40"
                />
                {modifier.label}
              </label>
            ))}
          </div>
        </div>
      </div>

      {presetKey === "custom" && (
        <Textarea
          value={binding.value}
          onChange={onManualChange}
          onBlur={binding.onBlur}
          rows={3}
          placeholder="Friendly, concise, never blames the user."
        />
      )}
    </div>
  )
}

function composeVoiceTone(presetKey: Exclude<PresetKey, "custom">, depth: ToneDepth, modifiers: ModifierKey[]) {
  const preset = PRESETS.find((p) => p.key === presetKey) ?? PRESETS[4]
  const depthOption = DEPTHS.find((d) => d.key === depth) ?? DEPTHS[1]
  const modifierOptions = modifiers
    .map((key) => MODIFIERS.find((modifier) => modifier.key === key))
    .filter((modifier): modifier is (typeof MODIFIERS)[number] => Boolean(modifier))
  const modifierLabels = [depthOption.label, ...modifierOptions.map((modifier) => modifier.label)]
  const instructions = [preset.instruction, depthOption.instruction, ...modifierOptions.map((modifier) => modifier.instruction)]

  return [
    `Preset: ${preset.name}.`,
    `Modifiers: ${modifierLabels.join(", ")}.`,
    "",
    instructions.join(" "),
  ].join("\n")
}

function parseGeneratedValue(value: string): {
  presetKey: PresetKey
  depth: ToneDepth
  modifiers: ModifierKey[]
} {
  const presetLine = value.match(/^Preset:\s*(.+?)\.\s*$/m)
  const modifierLine = value.match(/^Modifiers:\s*(.+?)\.\s*$/m)
  const presetName = presetLine?.[1]?.trim()
  const preset = PRESETS.find((p) => p.name === presetName)
  if (!preset || !modifierLine) {
    return { presetKey: "custom", depth: DEFAULT_DEPTH, modifiers: DEFAULT_MODIFIERS }
  }

  const labels = modifierLine[1].split(",").map((label) => label.trim()).filter(Boolean)
  const foundDepth = DEPTHS.find((option) => labels.includes(option.label))?.key ?? DEFAULT_DEPTH
  const foundModifiers = MODIFIERS.filter((modifier) => labels.includes(modifier.label)).map((modifier) => modifier.key)

  return {
    presetKey: preset.key,
    depth: foundDepth,
    modifiers: foundModifiers,
  }
}
